import {
  getTemplateTagChannelBranches,
  getTemplateTagGroupLabel,
  getTemplateTagLabel,
  getTemplateTagsByGroup,
  TEMPLATE_TAG,
  TEMPLATE_TAG_GROUP,
  type TemplateTag,
} from 'src/data/template';

import { createSidebarTemplateTagTab, type SidebarTemplateTagTab } from './sidebar';

export interface SidebarTreeLeaf {
  id: string;
  tab: SidebarTemplateTagTab;
}

export interface SidebarTreeBranchNode {
  id: string;
  label: string;
  children: SidebarTreeLeaf[];
}

export type SidebarTreeNode = SidebarTreeLeaf | SidebarTreeBranchNode;

export const isSidebarTreeBranch = (node: SidebarTreeNode): node is SidebarTreeBranchNode =>
  'children' in node;

const createLeaf = (tab: SidebarTemplateTagTab): SidebarTreeLeaf => ({
  id: `${tab.templateTag}:${tab.channelBranch?.label ?? ''}`,
  tab,
});

// A tag holding more templates than one grid can be read through becomes a branch over the platforms
// its templates were written for; every other tag stays a leaf that shows the whole tag.
const createTagNode = (templateTag: TemplateTag): SidebarTreeNode => {
  const channelBranches = getTemplateTagChannelBranches(templateTag);

  if (!channelBranches.length) {
    return createLeaf(createSidebarTemplateTagTab(templateTag));
  }

  return {
    id: templateTag,
    label: getTemplateTagLabel(templateTag),
    children: channelBranches.map((channelBranch) =>
      createLeaf(createSidebarTemplateTagTab(templateTag, channelBranch))
    ),
  };
};

const createTagGroupNode = (
  tagGroup: typeof TEMPLATE_TAG_GROUP.b2b | typeof TEMPLATE_TAG_GROUP.b2c
): SidebarTreeBranchNode => ({
  id: tagGroup,
  label: getTemplateTagGroupLabel(tagGroup),
  children: getTemplateTagsByGroup(tagGroup).map((templateTag) =>
    createLeaf(createSidebarTemplateTagTab(templateTag))
  ),
});

/**
 * The tag section of the popup's sidebar, in the order it is read: the channels a template is written
 * for, then the two audience taxonomies, and last the catch-all that belongs under neither.
 *
 * `channelTags` is passed in rather than read here because which of them a customer sees depends on
 * their feature flags.
 */
export const buildSidebarTreeNodes = (channelTags: TemplateTag[]): SidebarTreeNode[] => [
  ...channelTags.filter((tag) => tag !== TEMPLATE_TAG.channelOther).map(createTagNode),
  createTagGroupNode(TEMPLATE_TAG_GROUP.b2b),
  createTagGroupNode(TEMPLATE_TAG_GROUP.b2c),
  ...channelTags.filter((tag) => tag === TEMPLATE_TAG.channelOther).map(createTagNode),
];

/**
 * Drops the leaves holding no template, and any branch left with none. A leaf naming a platform the
 * customer has nothing for is a dead end: the tree is the only way to find out it is empty.
 */
export const pruneEmptySidebarTreeNodes = (
  nodes: SidebarTreeNode[],
  hasTemplates: (leaf: SidebarTreeLeaf) => boolean
): SidebarTreeNode[] =>
  nodes
    .map((node) =>
      isSidebarTreeBranch(node) ? { ...node, children: node.children.filter(hasTemplates) } : node
    )
    .filter((node) => (isSidebarTreeBranch(node) ? node.children.length > 0 : hasTemplates(node)));

/** The branch a leaf sits in, so opening the popup on a tag can show the branch already expanded. */
export const findSidebarTreeBranchId = (
  nodes: SidebarTreeNode[],
  isSelectedLeaf: (leaf: SidebarTreeLeaf) => boolean
): string | null => {
  const branch = nodes
    .filter(isSidebarTreeBranch)
    .find(({ children }) => children.some(isSelectedLeaf));

  return branch?.id ?? null;
};
